import { useCallback, useEffect, useRef, useState } from "react";

const HOVER_GRACE_MS = 220;
/** Longer grace for interactive tooltips (copy/link content) so the pointer can cross the gap. */
const INTERACTIVE_HOVER_GRACE_MS = 500;
const POLL_MS = 250;

/**
 * Closes a portaled tooltip when the pointer leaves, the window blurs, the user
 * scrolls, presses Escape, clicks outside, or the trigger is removed from the DOM.
 *
 * `tooltipRef` can be a React ref OR a function returning the live element
 * (useful when the portaled tooltip lives in a child component and the parent
 * does not re-render when it appears).
 */
export function useTooltipAutoClose({
    isOpen,
    onClose,
    triggerRef,
    tooltipRef,
    interactive = false,
    /** When false, only scroll/blur/Escape/outside-click close — no :hover polling. */
    enableHoverPoll = true,
    /** Override hover grace; defaults to 500ms when interactive, else 220ms. */
    graceMs,
}) {
    const graceTimerRef = useRef(null);
    const resolvedGraceMs =
        graceMs ?? (interactive ? INTERACTIVE_HOVER_GRACE_MS : HOVER_GRACE_MS);

    useEffect(() => {
        if (!isOpen) {
            if (graceTimerRef.current) {
                clearTimeout(graceTimerRef.current);
                graceTimerRef.current = null;
            }
            return undefined;
        }

        const resolveTooltipEl = () => {
            if (typeof tooltipRef === "function") {
                try {
                    return tooltipRef() ?? null;
                } catch (e) {
                    return null;
                }
            }
            return tooltipRef?.current ?? null;
        };

        const cancelGraceClose = () => {
            if (graceTimerRef.current) {
                clearTimeout(graceTimerRef.current);
                graceTimerRef.current = null;
            }
        };

        const scheduleGraceClose = () => {
            if (graceTimerRef.current) {
                return;
            }
            graceTimerRef.current = setTimeout(() => {
                graceTimerRef.current = null;
                const trigger = triggerRef?.current;
                if (!trigger || !document.contains(trigger)) {
                    onClose();
                    return;
                }
                const triggerHovered = trigger.matches(":hover");
                const tooltipEl = resolveTooltipEl();
                if (interactive && !tooltipEl) {
                    return;
                }
                const tooltipHovered = interactive && tooltipEl?.matches(":hover");
                if (!triggerHovered && !tooltipHovered) {
                    onClose();
                }
            }, resolvedGraceMs);
        };

        const forceClose = () => {
            cancelGraceClose();
            onClose();
        };

        const onKeyDown = (event) => {
            if (event.key === "Escape") {
                forceClose();
            }
        };

        const onScroll = () => forceClose();
        const onWheel = () => forceClose();
        const onTouchMove = () => forceClose();
        const onBlur = () => forceClose();

        const onVisibilityChange = () => {
            if (document.visibilityState === "hidden") {
                forceClose();
            }
        };

        const onPointerDown = (event) => {
            const trigger = triggerRef?.current;
            const tooltipEl = resolveTooltipEl();
            if (trigger?.contains(event.target)) {
                return;
            }
            if (interactive && tooltipEl?.contains(event.target)) {
                return;
            }
            forceClose();
        };

        const onDocMouseLeave = (event) => {
            if (!event.relatedTarget) {
                forceClose();
            }
        };

        const poll = () => {
            if (!enableHoverPoll) {
                return;
            }
            const trigger = triggerRef?.current;
            if (!trigger || !document.contains(trigger)) {
                forceClose();
                return;
            }
            const triggerHovered = trigger.matches(":hover");
            const tooltipEl = resolveTooltipEl();
            if (interactive && !tooltipEl) {
                cancelGraceClose();
                return;
            }
            const tooltipHovered = interactive && tooltipEl?.matches(":hover");
            if (triggerHovered || tooltipHovered) {
                cancelGraceClose();
            } else {
                scheduleGraceClose();
            }
        };

        const pollInterval = enableHoverPoll ? setInterval(poll, POLL_MS) : null;

        window.addEventListener("keydown", onKeyDown);
        window.addEventListener("scroll", onScroll, true);
        window.addEventListener("wheel", onWheel, { passive: true });
        window.addEventListener("touchmove", onTouchMove, { passive: true });
        window.addEventListener("blur", onBlur);
        document.addEventListener("visibilitychange", onVisibilityChange);
        document.addEventListener("pointerdown", onPointerDown, true);
        document.documentElement.addEventListener("mouseleave", onDocMouseLeave);

        return () => {
            cancelGraceClose();
            if (pollInterval) {
                clearInterval(pollInterval);
            }
            window.removeEventListener("keydown", onKeyDown);
            window.removeEventListener("scroll", onScroll, true);
            window.removeEventListener("wheel", onWheel);
            window.removeEventListener("touchmove", onTouchMove);
            window.removeEventListener("blur", onBlur);
            document.removeEventListener("visibilitychange", onVisibilityChange);
            document.removeEventListener("pointerdown", onPointerDown, true);
            document.documentElement.removeEventListener("mouseleave", onDocMouseLeave);
        };
    }, [isOpen, onClose, triggerRef, tooltipRef, interactive, enableHoverPoll, resolvedGraceMs]);
}

export const TOOLTIP_FADE_MS = 120;

/** Tracks open state, portal mount, and fade-out before unmount. */
export function useTooltipPortalFade() {
    const [isOpen, setIsOpen] = useState(false);
    const [isVisible, setIsVisible] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const unmountTimerRef = useRef(null);
    const isOpenRef = useRef(false);

    const open = useCallback(() => {
        if (unmountTimerRef.current) {
            clearTimeout(unmountTimerRef.current);
            unmountTimerRef.current = null;
        }
        isOpenRef.current = true;
        setIsClosing(false);
        setIsOpen(true);
        setIsVisible(true);
    }, []);

    const close = useCallback(() => {
        if (!isOpenRef.current) {
            return;
        }
        isOpenRef.current = false;
        setIsOpen(false);
        setIsClosing(true);
        if (unmountTimerRef.current) {
            clearTimeout(unmountTimerRef.current);
        }
        unmountTimerRef.current = setTimeout(() => {
            unmountTimerRef.current = null;
            setIsVisible(false);
            setIsClosing(false);
        }, TOOLTIP_FADE_MS);
    }, []);

    useEffect(() => {
        return () => {
            if (unmountTimerRef.current) {
                clearTimeout(unmountTimerRef.current);
            }
        };
    }, []);

    return { isOpen, isVisible, isClosing, open, close };
}
